import { Loader2, Save } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { Button } from '@/ui/base/button';
import { useResponsiveModalFooter } from '@/ui/useResponsiveModalFooter';

interface RodapeDoFormularioEmModalProps {
  aoCancelar: () => void;
  salvando?: boolean;
  rotuloDeSalvar?: string;
  formId: string;
  /**
   * `false` (padrao) registra Cancelar/Salvar no rodape fixo do `ResponsiveModal`
   * (via `ResponsiveModalFooterContext`) — mesmo desenho do `ModalFormShell` do
   * prototipo. `true` renderiza os mesmos botoes inline, no fluxo normal do
   * formulario, para o caso de o mesmo formulario ser usado numa pagina inteira
   * (sem `ResponsiveModal` por perto para prover o contexto) — ex.: uma tela de
   * edicao. Nao muda o comportamento de quem ja usa o rodape dentro de um modal.
   */
  inline?: boolean;
}

/**
 * Rodape padrao dos formularios renderizados dentro de `ResponsiveModal`:
 * registra Cancelar/Salvar no rodape fixo do modal (via
 * `ResponsiveModalFooterContext`) em vez de deixar os botoes rolarem junto do
 * conteudo — mesmo desenho do `ModalFormShell` do prototipo.
 */
export function RodapeDoFormularioEmModal({
  aoCancelar,
  salvando = false,
  rotuloDeSalvar = 'Salvar',
  formId,
  inline = false,
}: RodapeDoFormularioEmModalProps) {
  const contextoDoRodape = useResponsiveModalFooter();

  const acoes = useMemo(
    () => (
      <>
        <Button type="button" variant="outline" onClick={aoCancelar} disabled={salvando}>
          Cancelar
        </Button>
        <Button type="submit" form={formId} disabled={salvando} aria-busy={salvando}>
          {salvando ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          <span aria-live="polite">{salvando ? 'Salvando…' : rotuloDeSalvar}</span>
        </Button>
      </>
    ),
    [aoCancelar, formId, rotuloDeSalvar, salvando],
  );

  useEffect(() => {
    if (inline || !contextoDoRodape) {
      return undefined;
    }
    contextoDoRodape.setFooter(acoes);
    return () => contextoDoRodape.setFooter(null);
  }, [acoes, contextoDoRodape, inline]);

  if (inline) {
    return (
      <div className="flex flex-col-reverse gap-2 border-t border-border pt-6 sm:flex-row sm:justify-end">
        {acoes}
      </div>
    );
  }

  return null;
}
